import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetEmissorOrderQuery, useListEmissorOrderBeneficiariesQuery, useUpdateEmissorOrderMutation, } from '../services/contractOrdersApi';
import { buildRechargeReportFilename, formatAccrualPeriod, resolveOrderIdentifier } from '../utils/fileNamingUtils';
import { useEmissorFlow } from './useEmissorFlow';
import { useEmissorOrderReportPdf } from './useEmissorOrderReportPdf';
const BENEFICIARIES_LIMIT = 100;
const SEARCH_DEBOUNCE_MS = 400;
export function useEmissorOrderDetailsScreen({ orderId, screenStatusFromNavigation, }) {
    const { emissorContractId, emissorContractLegalName, isLoading: isFlowLoading } = useEmissorFlow();
    const contractId = emissorContractId ?? '';
    const shouldFetch = Boolean(contractId) && Boolean(orderId);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [resolvedScreenStatus, setResolvedScreenStatus] = useState(screenStatusFromNavigation ?? null);
    const { isError: isReportError, isLoading: isReportLoading, fetchReport } = useEmissorOrderReportPdf();
    const [reportErrorVisible, setReportErrorVisible] = useState(false);
    const [updateOrder, { isLoading: isUpdatingCreditDate }] = useUpdateEmissorOrderMutation();
    useEffect(() => {
        if (screenStatusFromNavigation) {
            setResolvedScreenStatus(screenStatusFromNavigation);
        }
    }, [screenStatusFromNavigation]);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchTerm]);
    useEffect(() => {
        if (isReportError) {
            setReportErrorVisible(true);
        }
    }, [isReportError]);
    const { data: order, isLoading: isOrderQueryLoading, isFetching: isOrderFetching, isError: isOrderError, refetch: refetchOrder, } = useGetEmissorOrderQuery({ contractId, orderId }, { skip: !shouldFetch });
    const { data: beneficiariesData, isLoading: isBeneficiariesLoading, isFetching: isBeneficiariesFetching, } = useListEmissorOrderBeneficiariesQuery({
        contractId,
        orderId,
        limit: BENEFICIARIES_LIMIT,
        name: debouncedSearchTerm || undefined,
    }, { skip: !shouldFetch });
    const beneficiaries = useMemo(() => {
        const items = beneficiariesData?.result ?? [];
        return [...items].sort((a, b) => {
            const nameA = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim().toLowerCase();
            const nameB = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim().toLowerCase();
            return nameA.localeCompare(nameB, 'pt-BR');
        });
    }, [beneficiariesData?.result]);
    const updateCreditDate = useCallback(async (scheduledCreditDate) => {
        if (!contractId || !orderId || !scheduledCreditDate) {
            return false;
        }
        try {
            const result = await updateOrder({
                contractId,
                orderId,
                body: { scheduledCreditDate },
            }).unwrap();
            if (result.screenStatus) {
                setResolvedScreenStatus(result.screenStatus);
            }
            await refetchOrder();
            return true;
        }
        catch {
            return false;
        }
    }, [contractId, orderId, refetchOrder, updateOrder]);
    const downloadReport = useCallback(() => {
        if (!contractId || !order) {
            return;
        }
        setReportErrorVisible(false);
        const filename = buildRechargeReportFilename(emissorContractLegalName ?? order.contract?.legalName ?? '', formatAccrualPeriod(order.competenceDate), resolveOrderIdentifier(order.description, order.protocol));
        fetchReport({ contractId, orderId }, filename);
    }, [contractId, emissorContractLegalName, fetchReport, order, orderId]);
    return {
        order: order ?? null,
        contractId,
        screenStatus: resolvedScreenStatus,
        beneficiaries,
        beneficiariesTotal: beneficiariesData?.total ?? beneficiaries.length,
        isOrderLoading: isFlowLoading || (shouldFetch && (isOrderQueryLoading || isOrderFetching)),
        isOrderError,
        isBeneficiariesLoading: shouldFetch && (isBeneficiariesLoading || isBeneficiariesFetching),
        searchTerm,
        debouncedSearchTerm,
        setSearchTerm,
        refetchOrder: () => {
            refetchOrder();
        },
        isUpdatingCreditDate,
        updateCreditDate,
        isReportLoading,
        isReportError: reportErrorVisible,
        clearReportError: () => setReportErrorVisible(false),
        downloadReport,
        contractLegalName: emissorContractLegalName,
    };
}
